import worldCountries from "./worldCountries.json";

export const WORLD_LOCATION_COUNTRIES = Object.entries(worldCountries)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const PROFILE_LOCATION_COUNTRIES = [
  { value: "SK", label: "Slovakia" },
  { value: "CZ", label: "Czechia" },
  { value: "PL", label: "Poland" },
  { value: "AT", label: "Austria" },
  { value: "HU", label: "Hungary" },
  { value: "DE", label: "Germany" },
  { value: "SI", label: "Slovenia" },
  { value: "HR", label: "Croatia" },
  { value: "NL", label: "Netherlands" },
  { value: "BE", label: "Belgium" },
  { value: "FR", label: "France" },
  { value: "IT", label: "Italy" },
  { value: "ES", label: "Spain" },
  { value: "PT", label: "Portugal" },
  { value: "IE", label: "Ireland" },
  { value: "CH", label: "Switzerland" },
  { value: "RO", label: "Romania" },
  { value: "BG", label: "Bulgaria" },
  { value: "GR", label: "Greece" },
  { value: "RS", label: "Serbia" },
  { value: "UA", label: "Ukraine" },
  { value: "LT", label: "Lithuania" },
  { value: "LV", label: "Latvia" },
  { value: "EE", label: "Estonia" },
  { value: "DK", label: "Denmark" },
  { value: "SE", label: "Sweden" },
  { value: "NO", label: "Norway" },
  { value: "FI", label: "Finland" },
  { value: "GB", label: "United Kingdom" },
];

export const PROFILE_CITY_OPTIONS_BY_COUNTRY = {
  SK: [
    "Bratislava", "Košice", "Prešov", "Žilina", "Nitra",
    "Banská Bystrica", "Trnava", "Trenčín",
  ],
  CZ: ["Prague", "Brno", "Ostrava", "Plzeň", "Liberec"],
  PL: ["Warsaw", "Kraków", "Wrocław", "Gdańsk", "Poznań", "Łódź", "Katowice", "Lublin"],
  AT: ["Vienna", "Graz", "Salzburg", "Innsbruck", "Linz"],
  HU: ["Budapest", "Debrecen", "Szeged", "Pécs"],
  DE: ["Berlin", "Munich", "Hamburg", "Frankfurt", "Cologne", "Düsseldorf", "Stuttgart", "Leipzig"],
  SI: ["Ljubljana", "Maribor", "Koper"],
  HR: ["Zagreb", "Split", "Rijeka", "Dubrovnik"],
  NL: ["Amsterdam", "Rotterdam", "The Hague", "Utrecht", "Eindhoven"],
  BE: ["Brussels", "Antwerp", "Ghent", "Bruges"],
  FR: ["Paris", "Lyon", "Marseille", "Toulouse", "Nice", "Strasbourg"],
  IT: ["Rome", "Milan", "Naples", "Turin", "Bologna", "Florence"],
  ES: ["Madrid", "Barcelona", "Valencia", "Seville", "Málaga", "Bilbao"],
  PT: ["Lisbon", "Porto", "Braga"],
  IE: ["Dublin", "Cork", "Galway"],
  CH: ["Zurich", "Geneva", "Basel", "Bern", "Lausanne"],
  RO: ["Bucharest", "Cluj-Napoca", "Timișoara", "Iași", "Brașov"],
  BG: ["Sofia", "Plovdiv", "Varna", "Burgas"],
  GR: ["Athens", "Thessaloniki", "Patras"],
  RS: ["Belgrade", "Novi Sad", "Niš"],
  UA: ["Kyiv", "Lviv", "Odesa", "Dnipro"],
  LT: ["Vilnius", "Kaunas", "Klaipėda"],
  LV: ["Riga", "Daugavpils"],
  EE: ["Tallinn", "Tartu"],
  DK: ["Copenhagen", "Aarhus"],
  SE: ["Stockholm", "Gothenburg", "Malmö"],
  NO: ["Oslo", "Bergen"],
  FI: ["Helsinki", "Tampere"],
  GB: ["London", "Manchester", "Birmingham", "Edinburgh", "Glasgow"],
  RU: [
    "Yekaterinburg",
    "Moscow",
    "Saint Petersburg",
    "Novosibirsk",
    "Kazan",
    "Nizhny Novgorod",
    "Chelyabinsk",
    "Samara",
    "Ufa",
    "Rostov-on-Don",
    "Perm",
    "Tyumen",
    "Omsk",
    "Krasnoyarsk",
    "Krasnodar",
    "Voronezh",
    "Volgograd",
    "Saratov",
    "Tolyatti",
    "Izhevsk",
    "Barnaul",
    "Ulyanovsk",
    "Irkutsk",
    "Khabarovsk",
    "Vladivostok",
    "Yaroslavl",
    "Makhachkala",
    "Tomsk",
    "Kaliningrad",
  ],
};

// RU is supported internally but never appears in the public country picker.
// City search can return Russian cities; the selected city sets the contour.
const INTERNAL_LOCATION_COUNTRIES = [{ value: "RU", label: "Russia" }];
export const ALL_LOCATION_COUNTRIES = [
  ...PROFILE_LOCATION_COUNTRIES,
  ...INTERNAL_LOCATION_COUNTRIES,
  ...WORLD_LOCATION_COUNTRIES.filter((country) => !PROFILE_CITY_OPTIONS_BY_COUNTRY[country.value]),
];

export const PROFILE_LOCATION_COUNTRY_CODES = new Set(
  ALL_LOCATION_COUNTRIES.map((country) => country.value)
);

const COUNTRY_ALIASES = new Map(
  ALL_LOCATION_COUNTRIES.flatMap((country) => [
    [country.value, country.value],
    [country.label.toUpperCase(), country.value],
  ])
);
COUNTRY_ALIASES.set("SLOVENSKO", "SK");

export const normalizeProfileCountry = (value) =>
  COUNTRY_ALIASES.get(String(value || "").trim().toUpperCase()) || "";

export const getProfileCountry = (value) => {
  const countryCode = normalizeProfileCountry(value);
  return ALL_LOCATION_COUNTRIES.find((country) => country.value === countryCode) || null;
};

export const getProfileCityOptions = (country) =>
  (PROFILE_CITY_OPTIONS_BY_COUNTRY[normalizeProfileCountry(country)] || []).map((city) => ({
    value: city,
    label: city,
  }));

const cityKey = (value) =>
  String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase();

const BRATISLAVA_LEGACY_CITY_KEYS = new Set([
  "bratis", "petrzalka", "stare mesto", "dubravka", "karlova ves",
  "nove mesto", "ruzinov", "vrakuna", "podunajske biskupice",
  "vajnory", "raca", "lamac", "zahorska bystrica",
  "devinska nova ves", "devin", "jarovce", "rusovce", "cunovo",
  "bratislava - vajnory", "bratislava - nove mesto",
]);

export const normalizeProfileCity = (country, value) => {
  if (normalizeProfileCountry(country) === "SK" && BRATISLAVA_LEGACY_CITY_KEYS.has(cityKey(value))) {
    return "Bratislava";
  }
  const city = getProfileCityOptions(country).find((option) => cityKey(option.value) === cityKey(value));
  // Worldwide values have already been validated against the server catalogue.
  return city?.value || (normalizeProfileCountry(country) ? String(value || "").trim() : "");
};
